import React, { useEffect, useMemo, useRef } from 'react';
import { Mic2 } from 'lucide-react';
import WordBreakdown from '../WordBreakdown';
import {
  buildSlokaLyricLines,
  buildMeaningLyricLines,
  findActiveLineIndex,
  seekTimeForWord
} from '../../utils/buildLyricLines';
import { flattenVerseWords, tokenizeParagraph } from '../../utils/tokenizeVerse';
import { getActiveWordIndex, getPlaybackProgress, estimateTrackDuration } from '../../utils/getActiveWordIndex';
import { DEFAULT_LANGUAGE } from '../../utils/defaultLanguage';
import { useTrackLyrics } from '../../hooks/useTrackLyrics';
import ChapterArtBackdrop from './ChapterArtBackdrop';

function LyricWord({ word, state, isLive, isMeaning }) {
  const meaningClass = isMeaning ? 'live-lyric-word--meaning' : '';

  if (!isLive) {
    return (
      <span className={`live-lyric-word live-lyric-word--static ${meaningClass}`}>
        {word}
      </span>
    );
  }

  return (
    <span className={`live-lyric-word live-lyric-word--${state} ${meaningClass}`}>
      {word}
    </span>
  );
}

function LyricsScrollPane({
  scrollRef,
  label,
  children,
  fitScreen,
  variant,
  withSpacers = false,
  isLiveSection = false,
  isPlaying = false,
  progress = 0
}) {
  return (
    <div
      className={`live-lyrics-pane live-lyrics-pane--${variant} ${
        isLiveSection ? 'live-lyrics-pane--live' : 'live-lyrics-pane--companion'
      }`}
    >
      {label && (
        <div className="live-lyrics-section-head">
          <p className="live-lyrics-section-label">{label}</p>
          {isLiveSection ? (
            <span
              className={`live-lyrics-sync-badge ${
                isPlaying ? 'live-lyrics-sync-badge--active' : 'live-lyrics-sync-badge--paused'
              }`}
            >
              {isPlaying ? 'In sync' : 'Paused'}
            </span>
          ) : (
            <span className="live-lyrics-sync-badge live-lyrics-sync-badge--waiting">
              {variant === 'meaning' ? 'After verse' : 'Reference'}
            </span>
          )}
        </div>
      )}

      {isLiveSection && (
        <div className="live-lyrics-section-progress" aria-hidden="true">
          <div
            className="live-lyrics-section-progress-fill"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      )}

      <div className="live-lyrics-pane-scroll-wrap">
        <div className="live-lyrics-fade live-lyrics-fade--top" aria-hidden="true" />
        <div className="live-lyrics-scroll" ref={scrollRef}>
          {withSpacers && (
            <div className={`live-lyrics-spacer ${fitScreen ? 'live-lyrics-spacer--fit' : ''}`} aria-hidden="true" />
          )}
          {children}
          {withSpacers && (
            <div
              className={`live-lyrics-spacer live-lyrics-spacer--bottom ${fitScreen ? 'live-lyrics-spacer--fit' : ''}`}
              aria-hidden="true"
            />
          )}
        </div>
        <div className="live-lyrics-fade live-lyrics-fade--bottom" aria-hidden="true" />
      </div>
    </div>
  );
}

const LiveLyricsPanel = ({
  track,
  currentTime,
  duration,
  seek,
  language = DEFAULT_LANGUAGE,
  isPlaying = false,
  layout = 'stream',
  showHeader = true,
  useChapterBackdrop = true,
  fitScreen = false,
  queueMeta = null,
  showWordBreakdown = false
}) => {
  const scrollRef = useRef(null);
  const slokaScrollRef = useRef(null);
  const meaningScrollRef = useRef(null);
  const activeLineRef = useRef(null);
  const activeWordRef = useRef(null);
  const prevTrackIdRef = useRef(null);

  const enrichedTrack = useTrackLyrics(track, language);
  const isWide = layout === 'compact-wide' || layout === 'stream-wide';

  const slokaLines = useMemo(
    () => buildSlokaLyricLines(enrichedTrack?.slokaText),
    [enrichedTrack?.slokaText]
  );
  const meaningLines = useMemo(
    () => buildMeaningLyricLines(enrichedTrack?.meaningText, isWide ? 16 : undefined),
    [enrichedTrack?.meaningText, isWide]
  );

  const slokaWords = useMemo(
    () => flattenVerseWords(enrichedTrack?.slokaText),
    [enrichedTrack?.slokaText]
  );
  const meaningWords = useMemo(
    () => tokenizeParagraph(enrichedTrack?.meaningText),
    [enrichedTrack?.meaningText]
  );

  const hasContent = Boolean(enrichedTrack?.slokaText || enrichedTrack?.meaningText);
  const highlightSloka = enrichedTrack?.type === 'sloka';

  const slokaProgress = useMemo(
    () =>
      highlightSloka
        ? getPlaybackProgress(currentTime, duration, slokaWords.length, 'sloka')
        : 0,
    [highlightSloka, currentTime, duration, slokaWords.length]
  );

  const meaningProgress = useMemo(
    () =>
      !highlightSloka
        ? getPlaybackProgress(currentTime, duration, meaningWords.length, 'meaning')
        : 0,
    [highlightSloka, currentTime, duration, meaningWords.length]
  );

  const activeWordIndex = highlightSloka
    ? getActiveWordIndex(currentTime, duration, slokaWords.length, 'sloka')
    : getActiveWordIndex(currentTime, duration, meaningWords.length, 'meaning');

  const slokaActiveLineIndex = highlightSloka
    ? findActiveLineIndex(slokaLines, activeWordIndex)
    : -1;
  const meaningActiveLineIndex = !highlightSloka
    ? findActiveLineIndex(meaningLines, activeWordIndex)
    : -1;

  const progress = highlightSloka ? slokaProgress : meaningProgress;

  const showSloka = slokaLines.length > 0;
  const showMeaning = meaningLines.length > 0;
  const showBoth = showSloka && showMeaning;

  const verseLabel =
    enrichedTrack?.chapterNumber != null
      ? `Chapter ${enrichedTrack.chapterNumber} · Verse ${enrichedTrack.slokaNumber}`
      : '';
  const phaseLabel = highlightSloka ? 'Recitation' : 'Meaning';

  useEffect(() => {
    if (!hasContent) return;

    const activeScroll = showBoth
      ? (highlightSloka ? slokaScrollRef.current : meaningScrollRef.current)
      : scrollRef.current;

    if (!activeScroll) return;

    if (activeWordRef.current) {
      activeWordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      return;
    }

    if (activeLineRef.current) {
      activeLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeWordIndex, slokaActiveLineIndex, meaningActiveLineIndex, enrichedTrack?.id, hasContent, highlightSloka, showBoth]);

  useEffect(() => {
    const trackId = enrichedTrack?.id;
    if (!trackId || trackId === prevTrackIdRef.current) return;

    prevTrackIdRef.current = trackId;

    if (showBoth) {
      const liveScroll = highlightSloka ? slokaScrollRef.current : meaningScrollRef.current;
      liveScroll?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [enrichedTrack?.id, highlightSloka, showBoth]);

  if (!hasContent) {
    const empty = <p className="live-lyrics-empty-text">Loading verse text…</p>;
    if (!useChapterBackdrop) return <div className="live-lyrics--empty">{empty}</div>;
    return (
      <ChapterArtBackdrop
        chapterNumber={enrichedTrack?.chapterNumber}
        slokaNumber={enrichedTrack?.slokaNumber}
        variant={layout === 'compact' || layout === 'compact-wide' ? 'maroon' : 'dark'}
        className="live-lyrics-backdrop live-lyrics--empty"
      >
        {empty}
      </ChapterArtBackdrop>
    );
  }

  const wordState = (globalIndex, isLive) => {
    if (!isLive) return 'static';
    if (!isPlaying && currentTime <= 0) return globalIndex === 0 ? 'active' : 'upcoming';
    if (activeWordIndex < 0) return 'idle';
    if (globalIndex < activeWordIndex) return 'past';
    if (globalIndex === activeWordIndex) return 'active';
    return 'upcoming';
  };

  const lineState = (lineIndex, activeLineIndex, isLive) => {
    if (!isLive || activeLineIndex < 0) return 'idle';
    if (activeWordIndex < 0) return 'idle';
    if (lineIndex < activeLineIndex) return 'past';
    if (lineIndex === activeLineIndex) return 'active';
    return 'upcoming';
  };

  const handleLineClick = (line, isLive, sectionWords, sectionType) => {
    if (!isLive || !seek || !line.words.length || !sectionWords.length) return;
    const firstWord = line.words[0].globalIndex;
    const effectiveDuration =
      duration > 0 ? duration : estimateTrackDuration(sectionWords.length, sectionType);
    seek(seekTimeForWord(firstWord, sectionWords.length, effectiveDuration));
  };

  const renderLines = (lines, isLive, activeLineIndex, sectionWords, sectionType) => {
    const isMeaning = sectionType === 'meaning';
    const meaningLineClass = isMeaning ? 'live-lyric-line--meaning' : '';

    return lines.map((line) => {
      const state = lineState(line.lineIndex, activeLineIndex, isLive);
      const isActiveLine = isLive && line.lineIndex === activeLineIndex;
      const content = line.words.map(({ word, globalIndex }) => {
        const stateForWord = wordState(globalIndex, isLive);
        const isActiveWord = isLive && globalIndex === activeWordIndex;

        if (isActiveWord) {
          return (
            <span key={`${line.id}-${globalIndex}`} ref={activeWordRef} className="live-lyric-word-wrap">
              <LyricWord word={word} state={stateForWord} isLive={isLive} isMeaning={isMeaning} />
            </span>
          );
        }

        return (
          <LyricWord
            key={`${line.id}-${globalIndex}`}
            word={word}
            state={stateForWord}
            isLive={isLive}
            isMeaning={isMeaning}
          />
        );
      });

      if (!isLive) {
        return (
          <p
            key={line.id}
            className={`live-lyric-line live-lyric-line--static live-lyric-line--${layout} ${meaningLineClass}`}
          >
            {content}
          </p>
        );
      }

      return (
        <button
          key={line.id}
          type="button"
          ref={isActiveLine ? activeLineRef : null}
          className={`live-lyric-line live-lyric-line--${state} live-lyric-line--${layout} ${meaningLineClass}`}
          onClick={() => handleLineClick(line, isLive, sectionWords, sectionType)}
        >
          {content}
        </button>
      );
    });
  };

  const slokaContent = renderLines(
    slokaLines,
    highlightSloka,
    slokaActiveLineIndex,
    slokaWords,
    'sloka'
  );

  const meaningContent = renderLines(
    meaningLines,
    !highlightSloka,
    meaningActiveLineIndex,
    meaningWords,
    'meaning'
  );

  const slokaPane = (
    <LyricsScrollPane
      scrollRef={slokaScrollRef}
      label="Verse"
      fitScreen={fitScreen}
      variant="sloka"
      withSpacers={highlightSloka}
      isLiveSection={highlightSloka}
      isPlaying={isPlaying}
      progress={slokaProgress}
    >
      {slokaContent}
    </LyricsScrollPane>
  );

  const meaningPane = (
    <LyricsScrollPane
      scrollRef={meaningScrollRef}
      label="Meaning"
      fitScreen={fitScreen}
      variant="meaning"
      withSpacers={!highlightSloka}
      isLiveSection={!highlightSloka}
      isPlaying={isPlaying}
      progress={meaningProgress}
    >
      {meaningContent}
    </LyricsScrollPane>
  );

  const headerEl = showHeader ? (
    <div className="live-lyrics-header">
      <Mic2 className={`w-4 h-4 shrink-0 ${isPlaying ? 'live-lyrics-mic-active' : ''}`} />
      <span className="live-lyrics-verse-title">{verseLabel}</span>
      <span className="live-lyrics-phase-pill">{phaseLabel}</span>
      {enrichedTrack.speaker && (
        <span className="live-lyrics-speaker">· {enrichedTrack.speaker}</span>
      )}
      {queueMeta && (
        <span className="live-lyrics-queue-meta">
          · {queueMeta.current}/{queueMeta.total}
        </span>
      )}
      {isPlaying && (
        <span className="live-lyrics-progress-pill">{Math.round(progress * 100)}%</span>
      )}
    </div>
  ) : null;

  const wordBreakdownEl = showWordBreakdown && enrichedTrack?.chapterNumber ? (
    <>
      <div className="live-lyrics-divider" role="separator" aria-hidden="true" />
      <div className="live-lyrics-pane live-lyrics-pane--meaning live-lyrics-pane--companion live-lyrics-word-breakdown-pane">
        <div className="live-lyrics-section-head live-lyrics-section-head--words">
          <p className="live-lyrics-section-label">Word by Word</p>
          <span className="live-lyrics-sync-badge live-lyrics-sync-badge--waiting">Sanskrit</span>
        </div>
        <div className="live-lyrics-pane-scroll-wrap live-lyrics-word-breakdown-scroll">
          <div className="live-lyrics-scroll">
            <WordBreakdown
              key={`${language}-${enrichedTrack.chapterNumber}-${enrichedTrack.slokaNumber}`}
              chapterNumber={enrichedTrack.chapterNumber}
              slokaNumber={enrichedTrack.slokaNumber}
              language={language}
              noToggle
              className="word-breakdown--dark"
            />
          </div>
        </div>
      </div>
    </>
  ) : null;

  const wordBreakdownWide = showWordBreakdown && enrichedTrack?.chapterNumber ? (
    <div className="live-lyrics-wide-words">
      <div className="live-lyrics-section-head live-lyrics-section-head--words">
        <p className="live-lyrics-section-label">Word by Word</p>
        <span className="live-lyrics-sync-badge live-lyrics-sync-badge--waiting">Sanskrit</span>
      </div>
      <WordBreakdown
        key={`${language}-${enrichedTrack.chapterNumber}-${enrichedTrack.slokaNumber}`}
        chapterNumber={enrichedTrack.chapterNumber}
        slokaNumber={enrichedTrack.slokaNumber}
        language={language}
        noToggle
        className="word-breakdown--dark"
      />
    </div>
  ) : null;

  const lyricsScroll = showBoth && isWide ? (
    <>
      <div className="listen-wide-sloka">
        {headerEl}
        {slokaPane}
      </div>
      {wordBreakdownWide}
      <div className="listen-wide-meaning">{meaningPane}</div>
    </>
  ) : showBoth ? (
    <div className="live-lyrics-scroll-wrap live-lyrics-split">
      {slokaPane}

      {wordBreakdownEl}

      <div className="live-lyrics-divider" role="separator" aria-hidden="true" />

      {meaningPane}
    </div>
  ) : (
    <div className="live-lyrics-scroll-wrap">
      <div className="live-lyrics-fade live-lyrics-fade--top" aria-hidden="true" />
      <div className="live-lyrics-scroll" ref={scrollRef}>
        <div className={`live-lyrics-spacer ${fitScreen ? 'live-lyrics-spacer--fit' : ''}`} aria-hidden="true" />
        {showSloka && slokaContent}
        {showWordBreakdown && enrichedTrack?.chapterNumber && (
          <>
            <div className="live-lyrics-divider" role="separator" aria-hidden="true" />
            <div className="live-lyrics-word-breakdown-inline">
              <div className="live-lyrics-section-head live-lyrics-section-head--words">
                <p className="live-lyrics-section-label">Word by Word</p>
                <span className="live-lyrics-sync-badge live-lyrics-sync-badge--waiting">Sanskrit</span>
              </div>
              <WordBreakdown
                key={`${language}-${enrichedTrack.chapterNumber}-${enrichedTrack.slokaNumber}`}
                chapterNumber={enrichedTrack.chapterNumber}
                slokaNumber={enrichedTrack.slokaNumber}
                language={language}
                noToggle
                className="word-breakdown--dark"
              />
            </div>
          </>
        )}
        {showMeaning && meaningContent}
        <div
          className={`live-lyrics-spacer live-lyrics-spacer--bottom ${fitScreen ? 'live-lyrics-spacer--fit' : ''}`}
          aria-hidden="true"
        />
      </div>
      <div className="live-lyrics-fade live-lyrics-fade--bottom" aria-hidden="true" />
    </div>
  );

  const lyricsBody = (
    <div
      className={`live-lyrics live-lyrics--${layout} ${isPlaying ? 'live-lyrics--playing' : ''} ${fitScreen ? 'live-lyrics--fit' : ''} ${showBoth && !isWide ? 'live-lyrics--split' : ''}`}
    >
      {!(isWide && showBoth) && headerEl}

      {lyricsScroll}
    </div>
  );

  if (!useChapterBackdrop) {
    return lyricsBody;
  }

  return (
    <ChapterArtBackdrop
      chapterNumber={enrichedTrack?.chapterNumber}
      slokaNumber={enrichedTrack?.slokaNumber}
      variant={layout === 'compact' || layout === 'compact-wide' ? 'maroon' : 'dark'}
      className="live-lyrics-backdrop"
    >
      {lyricsBody}
    </ChapterArtBackdrop>
  );
};

export default LiveLyricsPanel;
